/**
 * CR-6205: the files on one RFQ line, as the RFQ page shows them.
 *
 * Anyone who can open the RFQ — the buyer, an invited supplier — can download
 * them through the authenticated endpoint. While the RFQ is a draft the buyer
 * can also attach and remove files; once it is sent they are fixed, so a
 * supplier never quotes against a drawing that changed after it went out.
 */
import { useState } from 'react';
import { rfqAPI } from '../../lib/api';
import type { RFQDocument } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { Button } from '../ui/button';
import { Loader2, Paperclip, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import FileDropZone from '../common/FileDropZone';
import { MAX_ATTACHMENT_BYTES, downloadRfqDocument, formatBytes, uploadLineFiles } from './rfqDocumentFiles';

interface LineItemFilesProps {
  rfqId: number;
  lineId: number;
  lineNumber: number;
  documents: RFQDocument[];
  /** The buyer on a draft RFQ: may attach and remove files. */
  editable: boolean;
  /** Refetch the RFQ after a file is attached or removed. */
  onChanged: () => void;
}

const fileLabel = (doc: RFQDocument): string => doc.file_name || doc.title;

export default function LineItemFiles({ rfqId, lineId, lineNumber, documents, editable, onChanged }: LineItemFilesProps) {
  const [attaching, setAttaching] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const download = async (doc: RFQDocument) => {
    try {
      await downloadRfqDocument(doc);
    } catch (err: unknown) {
      console.error('Error downloading line file:', err);
      toast.error(`Couldn't download ${fileLabel(doc)}`);
    }
  };

  const attach = async (files: File[]) => {
    try {
      setAttaching(true);
      await uploadLineFiles(rfqId, lineId, files);
      toast.success(files.length === 1
        ? `${files[0].name} attached to line ${lineNumber}`
        : `${files.length} files attached to line ${lineNumber}`);
      onChanged();
    } catch (err: unknown) {
      console.error('Error attaching line file:', err);
      toast.error(getApiErrorMessage(err, 'Failed to attach the file'));
      onChanged();
    } finally {
      setAttaching(false);
    }
  };

  const remove = async (doc: RFQDocument) => {
    try {
      setRemovingId(doc.id);
      await rfqAPI.deleteDocument(doc.id);
      toast.success(`${fileLabel(doc)} removed from line ${lineNumber}`);
      onChanged();
    } catch (err: unknown) {
      console.error('Error removing line file:', err);
      toast.error(getApiErrorMessage(err, 'Failed to remove the file'));
    } finally {
      setRemovingId(null);
    }
  };

  if (!editable && documents.length === 0) return null;

  return (
    <div className="mt-2 space-y-1">
      {documents.map((doc) => (
        <div key={doc.id} className="flex items-center gap-2 text-xs">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto min-w-0 p-0 text-xs"
            onClick={() => download(doc)}
            aria-label={`Download ${fileLabel(doc)}`}
          >
            <Paperclip className="mr-1 h-3 w-3 shrink-0" />
            <span className="truncate" title={fileLabel(doc)}>{fileLabel(doc)}</span>
          </Button>
          <span className="shrink-0 text-muted-foreground">{formatBytes(doc.file_size)}</span>
          {editable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-1"
              aria-label={`Remove ${fileLabel(doc)} from line ${lineNumber}`}
              disabled={removingId === doc.id}
              onClick={() => remove(doc)}
            >
              {removingId === doc.id
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Trash2 className="h-3 w-3 text-destructive" />}
            </Button>
          )}
        </div>
      ))}
      {editable && (
        <FileDropZone
          size="compact"
          multiple
          maxSize={MAX_ATTACHMENT_BYTES}
          busy={attaching}
          hint="Drawings or specifications for this line. Up to 10 MB each."
          onFiles={attach}
          inputTestId={`line-${lineNumber}-attach-input`}
          aria-label={`Attach files to line ${lineNumber}`}
        />
      )}
    </div>
  );
}
